import React, { Fragment, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";

import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";
import { StoreDepartmentIssue } from "../../../../api/medicalStore/transaction/departmentIssueAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

/* ================= VALIDATION (SCHEMA ALIGNED) ================= */
const schema = yup.object().shape({
  vrType: yup.string().required("Voucher Type required"),
  vrNo: yup.number().required("Voucher No required"),
  vrDate: yup.date().required("Voucher Date required"),

  toDepartmentId: yup.string().required("To Department required"),

  remark: yup.string(),

  grossAmount: yup.number().required(),
  netAmount: yup.number().required(),

  itemDetails: yup.array().of(
    yup.object().shape({
      productId: yup.string().required("Item required"),
      unit: yup.number().required("Unit required"),
      quantity: yup.number().required("Quantity required"),
      rate: yup.number().required("Rate required"),
      amount: yup.number().required("Amount required"),
      batch: yup.number().required("Batch required")
    })
  )
});

const AddDepartmentIssueEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [departments, setDepartments] = useState([]);
  const [items, setItems] = useState([]);

  const {
    register,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      itemDetails: [
        {
          index: 1,
          productId: "",
          unit: 1,
          quantity: "",
          rate: "",
          amount: "",
          batch: ""
        }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "itemDetails"
  });

  useEffect(() => {
    GetDepartments().then(r => r.success && setDepartments(r.data));
    GetItems().then(r => r.success && setItems(r.data));
  }, []);

  /* ================= ROW CALCULATION ================= */
  const calculateRow = index => {
    const qty = Number(getValues(`itemDetails.${index}.quantity`) || 0);
    const rate = Number(getValues(`itemDetails.${index}.rate`) || 0);

    const amount = qty * rate;

    setValue(`itemDetails.${index}.amount`, amount.toFixed(2));
  };

  /* ================= TOTAL CALCULATION ================= */
  const watchedItems = useWatch({ control, name: "itemDetails" });

  useEffect(() => {
    let total = 0;
    watchedItems?.forEach(i => {
      total += Number(i.amount || 0);
    });

    setValue("grossAmount", total.toFixed(2));
    setValue("netAmount", total.toFixed(2));
  }, [watchedItems, setValue]);

  /* ================= SUBMIT ================= */
  const onSubmit = async data => {
    dispatch({ type: "LOADER_START" });
    try {
      const payload = {
        ...data,
        itemDetails: data.itemDetails.map((i, idx) => ({
          ...i,
          index: idx + 1
        }))
      };

      const response = await StoreDepartmentIssue(payload);
      if (response.success) {
        showToast("success", response.message);
        navigate(
          `/medical-store/transaction/department-issue-entry/list`
        );
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Add Department Issue" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Add Department Issue Entry</h5>
            <Link
              to={`/medical-store/transaction/department-issue-entry/list`}
            >
              <button className="btn btn-primary">List Department Issue</button>
            </Link>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="card-body">
              {/* HEADER */}
              <div className="row">
                <div className="col-3">
                  <label>Voucher Type</label>
                  <input className="form-control" {...register("vrType")} />
                </div>

                <div className="col-3">
                  <label>Voucher No</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("vrNo")}
                  />
                </div>

                <div className="col-3">
                  <label>Voucher Date</label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("vrDate")}
                  />
                </div>

                <div className="col-3">
                  <label>To Department</label>
                  <select
                    className="form-control"
                    {...register("toDepartmentId")}
                  >
                    <option value="">Select</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-6">
                  <label>Remark</label>
                  <input className="form-control" {...register("remark")} />
                </div>
              </div>

              {/* ITEMS */}
              <hr />
              <h6>Item Details</h6>

              {fields.map((item, index) => (
                <div className="row border p-2 mb-2" key={item.id}>
                  <div className="col-2">
                    <label>Item</label>
                    <select
                      className="form-control"
                      {...register(`itemDetails.${index}.productId`)}
                    >
                      <option value="">Select</option>
                      {items.map(i => (
                        <option key={i._id} value={i._id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-2">
                    <label>Unit</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.unit`)}
                    />
                  </div>

                  <div className="col-2">
                    <label>Qty</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.quantity`, {
                        onChange: () => calculateRow(index)
                      })}
                    />
                  </div>

                  <div className="col-2">
                    <label>Rate</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.rate`, {
                        onChange: () => calculateRow(index)
                      })}
                    />
                  </div>

                  <div className="col-2">
                    <label>Batch</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.batch`)}
                    />
                  </div>

                  <div className="col-2">
                    <label>Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`itemDetails.${index}.amount`)}
                      readOnly
                    />
                  </div>

                  <div className="col-1 text-center">
                    <button
                      type="button"
                      className="btn btn-danger mt-4"
                      onClick={() => remove(index)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  append({
                    index: fields.length + 1,
                    productId: "",
                    unit: 1,
                    quantity: "",
                    rate: "",
                    amount: "",
                    batch: ""
                  })
                }
              >
                + Add Item
              </button>

              {/* TOTALS */}
              <hr />
              <div className="row">
                <div className="col-3">
                  <label>Gross Amount</label>
                  <input
                    className="form-control"
                    {...register("grossAmount")}
                    readOnly
                  />
                </div>
                <div className="col-3">
                  <label>Net Amount</label>
                  <input
                    className="form-control"
                    {...register("netAmount")}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default AddDepartmentIssueEntry;
